import type { UpdatedProductKey } from '../stores/updatedProductStore';
import * as updatedProductStore from '../stores/updatedProductStore';
import type { OrganizationInfo } from '../types';
import { getStripeProductID } from './getStripeProductID';
import { formatOrganizationPlanType } from './organizationWorkspace';

function isUpdatedProductPlanType(
  planType: string | undefined,
): planType is UpdatedProductKey {
  return (
    planType != null &&
    (updatedProductStore.UPDATED_PRODUCT_KEYS as readonly string[]).includes(
      planType,
    )
  );
}

/**
 * List price for the first paid charge: per-seat list × seat count (team) or
 * one subscription unit (individual). Uses catalog prices after
 * `fetchProducts()`; returns null if catalog is not ready or plan is unknown.
 */
export function getTrialActivationChargeSummary(
  organizationInfo: OrganizationInfo,
) {
  const { planType, billingCycle, seatsPurchased, activeSeats } =
    organizationInfo;
  if (!isUpdatedProductPlanType(planType)) {
    return null;
  }
  let productId: string;
  try {
    productId = getStripeProductID(planType);
  } catch {
    return null;
  }
  const mode: 'month' | 'year' =
    billingCycle === 'yearly' ? 'year' : 'month';
  const price = updatedProductStore.getPriceByProductAndMode(
    productId,
    mode,
  );
  if (!price) {
    return null;
  }
  const isTeam = (
    updatedProductStore.TEAM_PRODUCT_KEYS as readonly string[]
  ).includes(planType);
  const seatCount = isTeam
    ? Math.max(1, seatsPurchased ?? activeSeats ?? 1)
    : 1;
  const totalCents = price.unit_amount * seatCount;
  const totalLabel = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (price.currency || 'usd').toUpperCase(),
  }).format(totalCents / 100);
  const planLabel = formatOrganizationPlanType(planType);
  const billingTypeLabel = mode === 'year' ? 'Yearly' : 'Monthly';
  return {
    planLabel,
    seatCount,
    billingTypeLabel,
    totalLabel,
  } as const;
}
